/** Trim and normalize a single label field from API/form data. */
export const normalizeProductLabel = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "";
};

/** Product name from deal payload (supports snake_case and legacy camelCase). */
export const getDealProductName = (deal = {}) =>
  normalizeProductLabel(deal.product_name ?? deal.productName);

/** Compact display for long product names in tables and modals. */
export const shortenProductLabel = (value, maxLength = 32) => {
  const normalized = normalizeProductLabel(value);
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trim()}…`;
};

/**
 * Deal title shown in UI — title remains the canonical display field.
 * Mirrors customer `getProductDisplayTitle` behavior.
 */
export const getDealDisplayTitle = (deal = {}) =>
  normalizeProductLabel(
    deal.title ?? deal.deal_title ?? deal.name ?? deal.product_title
  ) || "Untitled Deal";

/**
 * Vendor UI labels: show both title and product_name when they differ.
 * - Both present and different → title (headline) + product_name (subline)
 * - Only one present → that value as headline
 * - Same value → single headline
 */
export const getVendorDealTitleParts = (deal = {}) => {
  const productName = getDealProductName(deal);
  const rawTitle = normalizeProductLabel(
    deal.title ?? deal.deal_title ?? deal.name ?? deal.product_title
  );

  if (rawTitle && productName) {
    if (rawTitle.toLowerCase() === productName.toLowerCase()) {
      return { headline: rawTitle, subline: "", showSubline: false };
    }
    return { headline: rawTitle, subline: productName, showSubline: true };
  }

  if (rawTitle) {
    return { headline: rawTitle, subline: "", showSubline: false };
  }

  if (productName) {
    return { headline: productName, subline: "", showSubline: false };
  }

  return { headline: "Untitled Deal", subline: "", showSubline: false };
};

/** Single string for alt text, toasts, and compact contexts. */
export const getVendorDealLabel = (deal = {}) => {
  const { headline, subline, showSubline } = getVendorDealTitleParts(deal);
  return showSubline
    ? `${headline} · ${shortenProductLabel(subline)}`
    : headline;
};
